import { useEffect, useState } from "react";
import { api, ApiError, type Aircraft } from "../lib/api";
import { AircraftDialog } from "../components/AircraftDialog";
import { Alert, Button, PageTitle, Panel, StatusChip } from "../components/ui";
import { Link } from "react-router-dom";
import { MapPin, Plane, Plus } from "lucide-react";
import { placeName, relativeTime } from "../lib/missionFormat";

const CATEGORY_LABEL: Record<string, string> = {
  helicopter: "Helicopter",
  fixed_wing: "Fixed wing",
};

const UNIT_ABBREV: Record<string, string> = {
  litres: "L",
  gallons: "gal",
  pounds: "lbs",
  kilograms: "kg",
};

/** Trims the trailing zeros numeric(10,2) always brings back. */
function amount(value: string | null, unit: string | null): string | null {
  if (!value) return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return `${n.toLocaleString()} ${UNIT_ABBREV[unit ?? "litres"] ?? ""}`.trim();
}

/**
 * A count and its exceptions. The page is titled Aircraft and shows aircraft,
 * so a sentence explaining that would be filler — what earns the space is
 * state the reader cannot get by looking.
 */
function summarise(rows: Aircraft[]): string | null {
  if (rows.length === 0) return null;

  const counts = { unserviceable: 0, retired: 0 };
  for (const r of rows) {
    if (r.status === "unserviceable") counts.unserviceable++;
    if (r.status === "retired") counts.retired++;
  }

  const parts = [`${rows.length} aircraft`];
  // Only exceptions are named — "5 serviceable" on an all-serviceable fleet is
  // noise the reader has to check against nothing.
  if (counts.unserviceable > 0) parts.push(`${counts.unserviceable} unserviceable`);
  if (counts.retired > 0) parts.push(`${counts.retired} retired`);
  return parts.join(" · ");
}

/** Where the airframe was last recorded, suburb first, coordinates otherwise. */
function lastSeen(a: Aircraft): string {
  const where = placeName(a.lastLatitude, a.lastLongitude, a.lastSuburb);
  if (!where || !a.lastPositionAt) return "No position recorded yet";
  return `Last seen ${where} · ${relativeTime(a.lastPositionAt)}`;
}

export function Fleet() {
  const [rows, setRows] = useState<Aircraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Aircraft | null>(null);

  async function load() {
    try {
      setRows((await api.listAircraft()).aircraft);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load the fleet.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function openNew() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(a: Aircraft) {
    setEditing(a);
    setDialogOpen(true);
  }

  const summary = rows ? summarise(rows) : null;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <PageTitle detail={summary}>Aircraft</PageTitle>
        <Button onClick={openNew}>
          <Plus size={19} strokeWidth={2.6} aria-hidden />
          Add aircraft
        </Button>
      </div>

      {error && <Alert>{error}</Alert>}

      <Panel>
        {rows === null ? (
          <p className="p-6 text-[0.95rem] text-ink-soft">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <span className="inline-grid place-items-center w-14 h-14 rounded-lg bg-accent/20 text-accent-text mb-4">
              <Plane size={30} strokeWidth={2.2} aria-hidden />
            </span>
            <p className="text-[1.1rem] font-semibold text-ink mb-1.5">No aircraft yet</p>
            <p className="text-[0.95rem] text-ink-soft mb-6 max-w-sm mx-auto">
              Add your first airframe. It'll show up on any iPad signed in to this account.
            </p>
            <Button onClick={openNew}>Add aircraft</Button>
          </div>
        ) : (
          <ul className="divide-y divide-rule-soft">
            {rows.map((a) => {
              const fuel = amount(a.fuelCapacity, a.fuelUnit);
              const load = amount(a.loadCapacity, a.loadUnit);
              return (
                <li key={a.id} className="flex items-stretch">
                  <button
                    type="button"
                    onClick={() => openEdit(a)}
                    className="flex-1 min-w-0 text-left px-5 py-4 flex items-center gap-5 hover:bg-panel-raised transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="font-mono font-bold text-[1.05rem] text-ink">{a.registration}</span>
                        {a.callsign && (
                          <span className="text-[1rem] font-semibold text-ink-soft">{a.callsign}</span>
                        )}
                        <StatusChip status={a.status} />
                      </div>
                      <p className="mt-1 text-[0.9rem] text-ink-soft truncate">
                        {[a.model, CATEGORY_LABEL[a.category]].filter(Boolean).join(" · ")}
                      </p>
                      <p className="mt-0.5 inline-flex items-center gap-1.5 text-[0.9rem] text-ink-soft">
                        <MapPin size={15} strokeWidth={2.3} aria-hidden />
                        {lastSeen(a)}
                      </p>
                    </div>

                    <dl className="hidden sm:flex items-center gap-8 text-right">
                      <div>
                        <dt className="text-[0.8rem] text-ink-soft">Max fuel</dt>
                        <dd className="font-mono font-semibold text-[0.95rem] text-ink tabular-nums">{fuel ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-[0.8rem] text-ink-soft">Max load</dt>
                        <dd className="font-mono font-semibold text-[0.95rem] text-ink tabular-nums">{load ?? "—"}</dd>
                      </div>
                    </dl>
                  </button>
                  {/* Beside the row, not inside it: a link cannot sit in a button. */}
                  <Link
                    to={`/app/missions?aircraft=${a.id}`}
                    aria-label={`Missions flown by ${a.registration}`}
                    className="shrink-0 w-24 flex flex-col items-center justify-center border-l border-rule-soft text-center hover:bg-panel-raised transition-colors"
                  >
                    <span className="font-mono font-semibold text-[1rem] text-ink tabular-nums">{a.missionCount ?? "—"}</span>
                    <span className="text-[0.8rem] text-accent-text">Missions</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <AircraftDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
